import React, { useEffect, useState } from 'react';
import { Icons } from '@ohif/ui-next';

type ValidatorWarnings = string[];

// Shared state for warnings across components
let globalWarnings: ValidatorWarnings = [];
let globalDismissed = false;
const globalListeners: Set<() => void> = new Set();

const notifyListeners = () => {
  globalListeners.forEach(listener => listener());
};

const getWarningsFromAnnotationsData = (annotationsData: any): ValidatorWarnings => {
  try {
    const warnings = annotationsData?.data?.[0]?.study?.meta?.validator?.warnings || [];
    if (Array.isArray(warnings)) {
      return warnings.filter(w => typeof w === 'string' && w.trim().length > 0);
    }
    return [];
  } catch {
    return [];
  }
};

export const refreshWarnings = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const currentData = (window as any).annotationsData;
  const extracted = getWarningsFromAnnotationsData(currentData);
  globalWarnings = extracted;
  if (extracted.length === 0) {
    globalDismissed = false;
  }
  notifyListeners();
};

export const setDismissed = (value: boolean) => {
  globalDismissed = value;
  notifyListeners();
};

export const useWarningsState = () => {
  const [warnings, setWarningsState] = useState<ValidatorWarnings>(globalWarnings);
  const [dismissed, setDismissedState] = useState<boolean>(globalDismissed);

  useEffect(() => {
    const updateState = () => {
      setWarningsState([...globalWarnings]);
      setDismissedState(globalDismissed);
    };

    globalListeners.add(updateState);
    updateState();

    return () => {
      globalListeners.delete(updateState);
    };
  }, []);

  return { warnings, dismissed };
};

/**
 * Small floating banner that shows study-level validator warnings from
 * the annotations API response (`study.meta.validator.warnings`).
 *
 * It listens for:
 * - initial `window.annotationsData` set during viewer init
 * - a custom `annotations-data-updated` event fired when new data arrives
 */
const StudyValidatorWarnings: React.FC = () => {
  const { warnings, dismissed } = useWarningsState();

  useEffect(() => {
    refreshWarnings();

    if (typeof window === 'undefined') {
      return;
    }

    const handler = () => {
      setDismissed(false);
      refreshWarnings();
    };

    window.addEventListener('annotations-data-updated', handler as EventListener);

    return () => {
      window.removeEventListener('annotations-data-updated', handler as EventListener);
    };
  }, []);

  if (dismissed || !warnings.length) {
    return null;
  }

  return (
    <div
      className="pointer-events-auto fixed bottom-4 left-1/2 z-[9999] -translate-x-1/2 transform"
      style={{ maxWidth: '80vw' }}
    >
      <div className="flex max-w-3xl items-start gap-3 rounded-md border border-yellow-300 bg-black/80 px-4 py-3 text-sm text-yellow-100 shadow-lg backdrop-blur-md">
        <div className="mt-0.5 text-yellow-300">
          <Icons.ByName name="status-alert-warning" className="h-5 w-5 text-yellow-300" />
        </div>
        <div className="flex-1 space-y-1">
          <div className="font-semibold tracking-wide text-yellow-300">Dicom Validation Warnings</div>
          <ul className="list-disc space-y-0.5 pl-4">
            {warnings.map((w, idx) => (
              <li key={idx}>{w}</li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="ml-2 mt-0.5 text-xs font-medium uppercase tracking-wide text-yellow-200/80 hover:text-white"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default StudyValidatorWarnings;

